// ============================================================================
// One answer to "what has this supplier actually done with this document?"
// ============================================================================
// computeDocumentState (lib/compliance.js) has always been the canonical state
// machine. The bug was never in it — it was that every surface built its INPUT
// differently, and the copies drifted:
//
//   useSupplierCompliance  synthesised acknowledgements onto upload-shaped
//                          columns before calling it            (correct)
//   AdminCompliance        loaded acknowledgements separately and bucketed
//                          them by hand                          (its own rules)
//   SupplierDetail         never loaded acknowledgements at all, and ran a
//                          bespoke status ladder that checked sent_at and
//                          signed_at but not supplier_uploaded_file_url
//                                                                (wrong twice)
//
// So a document the supplier had signed read "Awaiting Review" in the queue,
// "Not Published" on their record, and counted as Outstanding in the totals —
// three screens disagreeing about one fact. Fixing the instances one screenshot
// at a time never converges, because the next surface starts a fourth copy.
//
// This module owns the synthesis. A document's evidence can arrive in three
// places and this is where they're reconciled:
//
//   supplier_onboarding_documents  uploads, and hosted-form / pdf_sign
//                                  signatures (they write a signed file back
//                                  onto the row)
//   document_acknowledgements      acknowledge-type documents, which never
//                                  touch the onboarding row at all
//   fusion_documents.satisfied_by  which of those to believe
//
// Everything here is pure. See [[one-helper-dedup]].
// ============================================================================

import { computeDocumentState, DOCUMENT_STATES } from './compliance';

// ⚠️ THE SAME COLUMN NAME MEANS DIFFERENT THINGS IN THE TWO TABLES. This is the
// single most confusing thing in this area and the source of several shipped
// bugs, so it is written down here rather than re-derived:
//
//   supplier_onboarding_documents.review_decision
//     CHECK: NULL | 'rejected' | 'needs_clarification'   ('approved' is INVALID)
//     → approval is expiry_approved_at being set, with no decision outstanding.
//       Writing 'approved' violates the CHECK — the auto-approval SQL and the
//       app's approve path both did, and both failed outright in production.
//
//   document_acknowledgements.review_decision
//     CHECK: NULL | 'approved' | 'rejected'   ('needs_clarification' is INVALID)
//     → approval IS the literal 'approved'. NULL means signed and undecided.
//
// (2026-05-supplier-onboarding-review-flow.sql and
//  2026-06-acknowledge-satisfaction-type.sql respectively.)
export const isApprovedAcknowledgement = (ack) => ack?.review_decision === 'approved';
export const isRejectedAcknowledgement = (ack) => ack?.review_decision === 'rejected';
// Signed, and Fusion hasn't decided yet.
export const isAwaitingReviewAcknowledgement = (ack) => !!ack && !ack.review_decision;

// An acknowledgement expressed in the upload-shaped columns computeDocumentState
// already understands, rather than teaching it a second vocabulary. This is also
// where the two review_decision vocabularies above are translated into the ONE
// the state machine speaks (the onboarding-row one):
//
//   signed, undecided   → supplier_uploaded_at set, expiry_approved_at null
//                         → AWAITING_REVIEW
//   'approved'          → expiry_approved_at + signed_at, decision cleared
//                         → APPROVED
//   'rejected'          → review_decision 'rejected' → OUTSTANDING (re-sign)
//
// The decision is deliberately NOT copied through verbatim: 'approved' is a
// legal value here but an illegal one on the onboarding row, so passing it
// along would put a value into the state machine that can't exist in the table
// it's modelled on.
//
// expiry_date is deliberately NOT invented: a one-off acknowledgement has no
// expiry, and inventing one would drop it into EXPIRING_SOON / EXPIRED.
const acknowledgementAsRecord = (ack, base = null) => {
  if (!ack) return base;
  const approved = isApprovedAcknowledgement(ack);
  return {
    // Keep the identity/administrative fields of any existing row so callers
    // that read document_key, not_required or ids off the result still work.
    ...(base || {}),
    document_key: base?.document_key || ack.document_key,
    supplier_uploaded_at: ack.acknowledged_at || null,
    signed_at: approved ? (ack.reviewed_at || ack.acknowledged_at) : null,
    expiry_approved_at: approved ? (ack.reviewed_at || ack.acknowledged_at) : null,
    // Only a PROBLEM crosses over — see the note above on the two vocabularies.
    review_decision: isRejectedAcknowledgement(ack) ? 'rejected' : null,
    // Marker so callers can tell a synthesised record from a real upload row
    // without re-deriving it (the UI wording differs: "signed" vs "uploaded").
    _fromAcknowledgement: true,
  };
};

// The record computeDocumentState should be given for this document.
//
// `satisfiedBy` decides precedence. For an acknowledge-type document the
// acknowledgement is the truth and any leftover upload row is stale — that
// happens when an admin converts an upload document to acknowledge, and letting
// both drive the state machine produces contradictory answers. For every other
// type the onboarding row is the truth, and an acknowledgement is only used if
// there's no row-level evidence at all.
export function effectiveRecord({ record = null, acknowledgement = null, satisfiedBy = null } = {}) {
  if (satisfiedBy === 'acknowledge') {
    if (!acknowledgement) {
      // No signature yet. Keep not_required (an admin can waive an
      // acknowledge-type document) but discard stale upload evidence so it
      // doesn't read as submitted.
      if (!record) return null;
      return { document_key: record.document_key, not_required: record.not_required || false };
    }
    return acknowledgementAsRecord(acknowledgement, {
      document_key: record?.document_key,
      not_required: record?.not_required || false,
    });
  }
  if (record) return record;
  // Non-acknowledge document with no onboarding row, but a signature exists —
  // rare, but it must not read as "nothing has happened".
  return acknowledgement ? acknowledgementAsRecord(acknowledgement) : null;
}

// State for one document, from whichever evidence exists.
export function documentStateFor({ record, acknowledgement, satisfiedBy }, options = {}) {
  return computeDocumentState(effectiveRecord({ record, acknowledgement, satisfiedBy }), options);
}

// Has the supplier done anything at all with this document? Distinct from
// "is it published to them" — a document can be signed without anyone having
// pressed Publish, which is exactly the case that read as "Not Published".
export function hasSupplierActioned({ record, acknowledgement } = {}) {
  return !!acknowledgement
    || !!(record && (record.supplier_uploaded_at || record.supplier_uploaded_file_url || record.signed_at));
}

// Latest row per document_key from a list ordered newest-first. Tolerates an
// unordered list by comparing the timestamp when both have one.
export function latestByKey(rows = [], tsField = 'acknowledged_at') {
  const m = new Map();
  for (const r of rows) {
    if (!r?.document_key) continue;
    const seen = m.get(r.document_key);
    if (!seen) { m.set(r.document_key, r); continue; }
    const a = r[tsField] ? new Date(r[tsField]).getTime() : 0;
    const b = seen[tsField] ? new Date(seen[tsField]).getTime() : 0;
    if (a > b) m.set(r.document_key, r);
  }
  return m;
}

// The whole picture for one supplier: one entry per required document, each
// carrying the evidence it was derived from so callers can render it without
// going back to the raw arrays.
//
//   requiredDocs     [{ key, label, satisfiedBy, ... }] — already filtered to
//                    LIVE documents by the caller (see lib/liveDocuments)
//   records          supplier_onboarding_documents rows
//   acknowledgements document_acknowledgements rows
export function supplierDocumentEntries({
  requiredDocs = [],
  records = [],
  acknowledgements = [],
  options = {},
} = {}) {
  const recordByKey = new Map();
  for (const r of records) if (r?.document_key && !recordByKey.has(r.document_key)) recordByKey.set(r.document_key, r);
  const ackByKey = latestByKey(acknowledgements);

  return requiredDocs.map((meta) => {
    const record = recordByKey.get(meta.key) || null;
    const acknowledgement = ackByKey.get(meta.key) || null;
    const satisfiedBy = meta.satisfiedBy || meta.satisfied_by || null;
    const effective = effectiveRecord({ record, acknowledgement, satisfiedBy });
    return {
      key: meta.key,
      meta,
      raw: record,
      acknowledgement,
      record: effective,
      state: computeDocumentState(effective, options),
      actioned: hasSupplierActioned({ record, acknowledgement }),
    };
  });
}

// Tally entries by state. Every state is present so callers can render a fixed
// row order without guarding each lookup.
export function countsByState(entries = []) {
  const counts = {};
  for (const s of Object.values(DOCUMENT_STATES)) counts[s] = 0;
  for (const e of entries) counts[e.state] = (counts[e.state] || 0) + 1;
  return counts;
}
